<script setup>
// "Why did you remove this recommended session?" survey (Asana UNB-599).
// Opened imperatively from useScheduleAction when a user removes an
// assistant-recommended session. It never blocks the removal — the session is
// removed whether the user Submits or dismisses; this only decides whether we
// captured a reason. The resolved value tells the caller which happened:
//   { submitted: true,  reasons: [...], otherText }  → Submit
//   { submitted: false }                             → Close / ✕ / Esc / backdrop
const props = defineProps({
    session: { type: Object, required: true },
});

const open = defineModel('open', { default: true });
const emit = defineEmits(['close']);

const title = computed(() => props.session?.title || 'this session');

// Multi-select. Labels are the survey choice strings, sent verbatim to PostHog
// + D1, so they must match the design copy exactly.
const REASONS = [
    'I’m not interested in the topic/it doesn’t align with my goals',
    'It’s not relevant for my role or industry',
    'The session format doesn’t appeal to me (i.e. Meetup)',
    'I need a break during this time slot/this time doesn’t work for me',
    'Other',
];
const OTHER = 'Other';

const selected = ref([]);
const otherText = ref('');

const isSelected = (label) => selected.value.includes(label);
const toggle = (label) => {
    selected.value = isSelected(label)
        ? selected.value.filter((l) => l !== label)
        : [...selected.value, label];
};

const showOther = computed(() => isSelected(OTHER));
// One reason is enough. The free-text box that "Other" reveals is optional
// (UNB): picking Other is itself an answer, and blocking Submit until the box
// had content meant someone who just wanted to say "something else" was stuck
// staring at a disabled button with nothing telling them why.
const canSubmit = computed(() => selected.value.length > 0);

// Default outcome = dismissed. Submit overwrites it BEFORE closing so the
// resolved payload carries the response; any other close path keeps this.
const result = ref({ submitted: false });

const submit = () => {
    if (!canSubmit.value) return;
    result.value = {
        submitted: true,
        reasons: [...selected.value],
        otherText: showOther.value ? otherText.value.trim() : '',
    };
    open.value = false;
};

// Fires once when ModalShell transitions open → closed (Submit, the Close link,
// the ✕, Esc, or a backdrop click all funnel through here). Resolve the
// overlay promise with whatever `result` holds at that point.
const onShellClose = () => emit('close', result.value);
</script>

<template>
    <FragmentsModalShell
        v-model:open="open"
        :headline="`Help us improve the Agenda Builder by letting us know why you removed “${title}”.`"
        headline-class="tracking-normal"
        @close="onShellClose"
    >
        <p class="font-hubspot-sans font-light text-[16px] leading-[1.5] text-(--magenta) m-0 mb-[24px]">
            You may select multiple options:
        </p>

        <div class="flex flex-col gap-[16px] w-full">
            <button
                v-for="reason in REASONS"
                :key="reason"
                type="button"
                role="checkbox"
                :aria-checked="isSelected(reason)"
                class="group w-full flex items-center gap-[14px] rounded-[10px] border border-(--magenta) px-[20px] py-[15px]
                    text-left font-hubspot-serif font-normal text-[16px] leading-[1.4] tracking-[0.32px]
                    cursor-pointer transition-colors duration-200
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)"
                :class="isSelected(reason)
                    ? 'bg-(--magenta) text-white'
                    : 'bg-transparent text-(--magenta) hover:bg-(--orange) hover:border-(--orange) hover:text-white'"
                @click="toggle(reason)"
            >
                <!-- Indicator: empty magenta ring → white disc + magenta check when selected -->
                <span
                    class="shrink-0 grid place-items-center size-[16px] rounded-full border transition-colors duration-200"
                    :class="isSelected(reason) ? 'bg-white border-white' : 'border-(--magenta) group-hover:border-white'"
                >
                    <svg v-if="isSelected(reason)" class="size-[10px] text-(--magenta)" viewBox="0 0 10 8" fill="none" aria-hidden="true">
                        <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </span>
                <span>{{ reason }}</span>
            </button>
        </div>

        <!-- Free-text, revealed only when "Other" is checked -->
        <textarea
            v-if="showOther"
            v-model="otherText"
            rows="4"
            placeholder="Please let us know why"
            class="w-full mt-[16px] rounded-[10px] bg-white border border-(--dark-pink) px-[20px] py-[15px] resize-y min-h-[100px]
                text-left font-hubspot-sans font-light text-[15px] leading-[23px] text-(--magenta) placeholder:text-(--magenta)/60
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)"
        />

        <template #actions>
            <div class="flex flex-col items-center gap-[16px]">
                <FragmentsPillButton label="Submit" :disabled="!canSubmit" @click="submit" />

                <button
                    type="button"
                    class="font-hubspot-sans font-light text-[15px] cursor-pointer
                        text-(--darker-pink) underline decoration-dotted underline-offset-2
                        hover:text-(--purple) hover:no-underline
                        transition-colors duration-200
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)"
                    @click="open = false"
                >Close</button>
            </div>
        </template>
    </FragmentsModalShell>
</template>
